(() => {
  "use strict";

  const ZONE = "America/Denver";
  const WEATHER_REFRESH_MS = 15 * 60 * 1000;
  const OPEN_METEO_URL = "https://api.open-meteo.com/v1/forecast?latitude=36.4072&longitude=-105.5731&current=temperature_2m,weather_code,is_day&timezone=America%2FDenver";
  const NWS_URL = "https://api.weather.gov/stations/KSKX/observations/latest";

  const icon = type => {
    const icons = {
      sun:'<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"></path></svg>',
      moon:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 15.2A8.5 8.5 0 0 1 8.8 4a8.5 8.5 0 1 0 11.2 11.2Z"></path></svg>',
      cloud:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 18h10a4 4 0 0 0 .7-7.94A6 6 0 0 0 6.4 8.4 4.5 4.5 0 0 0 7 18Z"></path></svg>',
      rain:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 15h10a4 4 0 0 0 .7-7.94A6 6 0 0 0 6.4 5.4 4.5 4.5 0 0 0 7 15Z"></path><path d="M8 18l-1 3M13 18l-1 3M18 18l-1 3"></path></svg>',
      snow:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 14h10a4 4 0 0 0 .7-7.94A6 6 0 0 0 6.4 4.4 4.5 4.5 0 0 0 7 14Z"></path><path d="M8 18h.01M12 20h.01M16 18h.01M19 21h.01"></path></svg>',
      hail:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 14h10a4 4 0 0 0 .7-7.94A6 6 0 0 0 6.4 4.4 4.5 4.5 0 0 0 7 14Z"></path><circle cx="8" cy="19" r="1"></circle><circle cx="13" cy="17" r="1"></circle><circle cx="18" cy="20" r="1"></circle></svg>',
      storm:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 14h10a4 4 0 0 0 .7-7.94A6 6 0 0 0 6.4 4.4 4.5 4.5 0 0 0 7 14Z"></path><path d="m13 15-3 5h3l-1 3 4-6h-3l1-2Z"></path></svg>',
      fog:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 9h14M3 13h18M6 17h12"></path></svg>'
    };
    return icons[type] || icons.cloud;
  };

  const wmo = code => {
    const n = Number(code);
    if (n === 0) return {label:"CLEAR", type:"clear"};
    if (n === 1) return {label:"MAINLY CLEAR", type:"clear"};
    if (n === 2) return {label:"PARTLY CLOUDY", type:"cloud"};
    if (n === 3) return {label:"OVERCAST", type:"cloud"};
    if (n === 45 || n === 48) return {label:"FOG", type:"fog"};
    if ([51,53,55].includes(n)) return {label:"DRIZZLE", type:"rain"};
    if ([56,57].includes(n)) return {label:"FREEZING DRIZZLE", type:"rain"};
    if ([61,63,65].includes(n)) return {label:"RAIN", type:"rain"};
    if ([66,67].includes(n)) return {label:"FREEZING RAIN", type:"rain"};
    if ([71,73,75,77].includes(n)) return {label:"SNOW", type:"snow"};
    if ([80,81,82].includes(n)) return {label:"RAIN SHOWERS", type:"rain"};
    if ([85,86].includes(n)) return {label:"SNOW SHOWERS", type:"snow"};
    if (n === 95) return {label:"THUNDERSTORM", type:"storm"};
    if (n === 96 || n === 99) return {label:"THUNDERSTORM + HAIL", type:"hail"};
    return {label:"CURRENT CONDITIONS", type:"cloud"};
  };

  const localHour = date => Number(new Intl.DateTimeFormat("en-US",{
    timeZone:ZONE,
    hour:"numeric",
    hourCycle:"h23"
  }).format(date));

  const isNightNow = () => {
    const h = localHour(new Date());
    return h < 6 || h >= 19;
  };

  const conditionIcon = (type, isDay) => {
    if (type === "clear") return isDay ? "sun" : "moon";
    return type;
  };

  const formatWeather = (celsius, label) => {
    const c = Number(celsius);
    if (!Number.isFinite(c)) return "--°F · --°C · UNAVAILABLE";
    const f = Math.round((c * 9 / 5) + 32);
    return `${f}°F · ${Math.round(c)}°C · ${label}`;
  };

  async function fetchOpenMeteo(signal) {
    const response = await fetch(OPEN_METEO_URL,{
      cache:"no-store",
      credentials:"omit",
      mode:"cors",
      signal
    });
    if (!response.ok) throw new Error("Open-Meteo request failed");
    const payload = await response.json();
    const current = payload && payload.current ? payload.current : {};
    const celsius = Number(current.temperature_2m);
    const condition = wmo(current.weather_code);
    const isDay = Number(current.is_day) === 1;
    if (!Number.isFinite(celsius)) throw new Error("Open-Meteo temperature unavailable");
    return {
      celsius,
      label:condition.label,
      iconType:conditionIcon(condition.type,isDay)
    };
  }

  async function fetchNws(signal) {
    const response = await fetch(NWS_URL,{
      headers:{Accept:"application/geo+json"},
      cache:"no-store",
      credentials:"omit",
      mode:"cors",
      signal
    });
    if (!response.ok) throw new Error("NWS request failed");
    const payload = await response.json();
    const props = payload && payload.properties ? payload.properties : {};
    const celsius = Number(props.temperature && props.temperature.value);
    if (!Number.isFinite(celsius)) throw new Error("NWS temperature unavailable");
    const description = String(props.textDescription || "CURRENT CONDITIONS").toUpperCase();
    const value = description.toLowerCase();
    let type = "cloud";
    if (value.includes("thunder")) type = "storm";
    else if (value.includes("hail") || value.includes("ice pellet")) type = "hail";
    else if (value.includes("snow") || value.includes("flurr")) type = "snow";
    else if (value.includes("rain") || value.includes("drizzle") || value.includes("shower")) type = "rain";
    else if (value.includes("fog") || value.includes("mist") || value.includes("haze") || value.includes("smoke")) type = "fog";
    else if (value.includes("clear") || value.includes("fair") || value.includes("sunny")) type = "clear";
    const iconUrl = String(props.icon || "");
    const isDay = iconUrl ? !iconUrl.includes("/night/") : !isNightNow();
    return {celsius,label:description,iconType:conditionIcon(type,isDay)};
  }

  const init = () => {
    const header = document.querySelector(".header.header--top-center");
    if (!header || header.dataset.htUtilitiesReady === "true") return;
    header.dataset.htUtilitiesReady = "true";

    const summary = header.querySelector(".header__icons > .header__search summary");
    if (summary) {
      summary.setAttribute("aria-label","Search HeyTaos");
      if (!summary.querySelector(".ht-header-search__label")) {
        const label = document.createElement("span");
        label.className = "ht-header-search__label";
        label.textContent = "Search HeyTaos";
        summary.append(label);
      }
    }

    const block = document.createElement("div");
    block.className = "ht-taos-now";
    block.setAttribute("role","status");
    block.setAttribute("aria-label","Current Taos weather and local time");
    block.innerHTML =
      '<div class="ht-taos-now__label">TAOS NOW</div>' +
      '<div class="ht-taos-now__weather">' +
      '<span class="ht-taos-now__icon" data-ht-taos-icon>' + icon(isNightNow() ? "moon" : "sun") + '</span>' +
      '<span data-ht-taos-weather>--°F · --°C · LOADING</span>' +
      '</div>' +
      '<div class="ht-taos-now__datetime" data-ht-taos-datetime></div>';
    header.prepend(block);

    const weather = block.querySelector("[data-ht-taos-weather]");
    const iconNode = block.querySelector("[data-ht-taos-icon]");
    const datetime = block.querySelector("[data-ht-taos-datetime]");

    const updateClock = () => {
      const now = new Date();
      const day = new Intl.DateTimeFormat("en-US",{
        timeZone:ZONE,
        weekday:"short",
        month:"short",
        day:"numeric"
      }).format(now).replace(/,/g,"").toUpperCase();
      const time = new Intl.DateTimeFormat("en-US",{
        timeZone:ZONE,
        hour:"numeric",
        minute:"2-digit",
        timeZoneName:"short"
      }).format(now).replace(/\s+/g," ").toUpperCase();
      datetime.textContent = `${day} · ${time}`;
    };

    const updateWeather = async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(),6500);
      try {
        let reading;
        try {
          reading = await fetchOpenMeteo(controller.signal);
        } catch (_) {
          reading = await fetchNws(controller.signal);
        }
        weather.textContent = formatWeather(reading.celsius,reading.label);
        iconNode.innerHTML = icon(reading.iconType);
        block.title = "Current Taos weather";
      } catch (_) {
        weather.textContent = "--°F · --°C · UNAVAILABLE";
        iconNode.innerHTML = icon(isNightNow() ? "moon" : "sun");
        block.title = "Taos local time. Current weather is temporarily unavailable.";
      } finally {
        clearTimeout(timer);
      }
    };

    updateClock();
    updateWeather();
    setInterval(updateClock,60 * 1000);
    setInterval(updateWeather,WEATHER_REFRESH_MS);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded",init,{once:true});
  } else {
    init();
  }
})();
